import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Regions, PortfolioComparisonItem, UsComparisonTaxResult, CaComparisonTaxResult } from '@capintel/instruments-lib';

import { Dispatch } from '@/redux';
import { setTaxAnalysisData, setTaxAnalysisHoldingsData } from '@/redux/modules/comparison';

import { useTaxAnalysisInputs } from '../useTaxAnalysisInputs/useTaxAnalysisInputs';
import { getTaxCalculationConfig, calculateTax, mapUsTransactionToHoldingTaxMetrics, mapCaTransactionToHoldingTaxMetrics } from '../utils/taxCalculationUtils';
import ComparisonItem from '@/types/comparison-item';
import { PortfolioTaxMetrics } from '@/types/tax-analysis';

interface UseTaxAnalysisDataParams {
  selectedItem?: ComparisonItem;
  targetComparisonItem?: ComparisonItem;
  isPortfolioEditorOpen: boolean;
  readOnly?: boolean;
}

/**
 * Hook to manage tax analysis calculations and dispatch results to Redux.
 * Tax metrics are stored in Redux and consumed from there by components.
 * Skips recalculation when the portfolio editor is open to prevent excessive reruns.
 * Supports both US and CA tax calculations based on user's app region.
 */
export const useTaxAnalysisData = ({ selectedItem, targetComparisonItem, isPortfolioEditorOpen, readOnly = false }: UseTaxAnalysisDataParams) => {
  const dispatch = useDispatch<Dispatch>();
  const taxInputs = useTaxAnalysisInputs();
  const regionInputs = taxInputs.regionInputs;

  if (!regionInputs?.region) {
    // If we don't have a region, we can't calculate tax - this can happen if user data is still loading or region is unsupported
    return;
  }

  // Use ref to store cached data without causing rerenders
  const lastCalculationRef = useRef<string>('');
  const wasEditorOpenRef = useRef<boolean>(false);

  // Create a key to check if we need to recalculate
  // Include holdings count and a simple hash to detect portfolio changes
  // Temporary workaround for recalculating when we close portfolio editor with new changes.
  const holdingsKey =
    selectedItem?.holdings
      ?.map(h => {
        const lotsKey = h.lots?.map(l => `${l._id ?? ''}:${l.costBasis ?? ''}:${l.units ?? ''}:${l.purchaseDate ?? ''}`).join(';') || '';
        return `${h.id}:${h.units ?? h.weight ?? ''}:${h.taxSettings?.costBasis ?? ''}:${h.taxSettings?.purchaseDate ?? ''}:${lotsKey}`;
      })
      .join(',') || '';
  const targetHoldingsKey = targetComparisonItem?.holdings?.map(h => `${h.id}:${h.units ?? h.weight ?? ''}`).join(',') || '';
  const regionInputsKey = `${regionInputs.region}|${JSON.stringify(regionInputs.values ?? {})}`;

  useEffect(() => {
    // Don't run calculator if readOnly
    if (readOnly) {
      return;
    }

    // If portfolio editor is open, don't recalculate
    if (isPortfolioEditorOpen) {
      wasEditorOpenRef.current = true;
      return;
    }

    // If editor was just closed, force recalculation by clearing the cache
    if (wasEditorOpenRef.current && !isPortfolioEditorOpen) {
      wasEditorOpenRef.current = false;
      lastCalculationRef.current = '';
    }

    if (!selectedItem || !selectedItem.holdings) {
      // Clear the cache when there's no selected item so recalculation happens when one is selected
      lastCalculationRef.current = '';
      return;
    }

    // Build tax calculation config - returns null if required rates are missing
    const taxConfig = getTaxCalculationConfig(regionInputs);
    if (!taxConfig) {
      return;
    }

    // Build calculation key based on region
    const calculationKey = [selectedItem._id, holdingsKey, targetComparisonItem?._id || '', targetHoldingsKey, regionInputsKey].join('|');

    // Skip if we already calculated for this exact combination
    if (lastCalculationRef.current === calculationKey) {
      return;
    }

    try {
      const comparisonResult = calculateTax(selectedItem as PortfolioComparisonItem, targetComparisonItem as PortfolioComparisonItem | undefined, taxConfig);

      if (targetComparisonItem) {
        const taxMetrics: PortfolioTaxMetrics = {
          taxImplications: comparisonResult.taxImplications,
          changelog: comparisonResult.changelog,
        };

        dispatch(setTaxAnalysisData(selectedItem._id, taxMetrics));
      }

      // Map transactions to holding tax metrics based on region
      const holdingsMetricsMappers = {
        [Regions.CA]: () => (comparisonResult as CaComparisonTaxResult).transactions.map(mapCaTransactionToHoldingTaxMetrics),
        [Regions.US]: () => (comparisonResult as UsComparisonTaxResult).transactions.map(mapUsTransactionToHoldingTaxMetrics),
      };

      const holdingsTaxMetrics = holdingsMetricsMappers[taxConfig.region]();

      dispatch(setTaxAnalysisHoldingsData(selectedItem._id, holdingsTaxMetrics));

      // Cache only after a successful calculation so transient failures can retry
      lastCalculationRef.current = calculationKey;
    } catch (error) {
      console.error('Error calculating tax analysis:', error);
    }
  }, [selectedItem?._id, targetComparisonItem?._id, regionInputsKey, isPortfolioEditorOpen, holdingsKey, targetHoldingsKey, dispatch]);
};
